import React from 'react';
import type { PasskeyCredentialSummary } from '@usacsmideo/types';
import {
  usePasskeyEnrollment,
  type PasskeyEnrollmentError,
  type PasskeyManagementError,
} from '../../hooks/usePasskeyEnrollment';
import { MfaChallengeForm } from '../auth/MfaChallengeForm';
import { Button } from '../buttons/Button';
import { PasswordInput } from '../forms/PasswordInput';

/**
 * Passkey enrollment + per-credential management for the SIGNED-IN patient —
 * CU-86ajzr8x1. Sibling of `<SmsEnrollmentSection variant="profile" />` on the
 * profile page.
 *
 * ── ⚠️ THE SAFETY PROPERTY IS THE ABSENCE OF A uid INPUT ──────────────────
 * There is no uid field, no search box and no lookup affordance here. The
 * subject is always `auth.currentUser`, derived inside the hook, and the server
 * binds to `req.auth.uid`. A `credId` inside a remove button's closure is NOT a
 * uid input and must never become a text field.
 *
 * ⚠️ REMOVAL IS PER-CREDENTIAL, NEVER ACCOUNT-WIDE. Each row removes exactly
 * one `credId`. It must never be wired to a revoke-all: that would also delete
 * this patient's MOBILE passkeys and sign them out everywhere. The substitution
 * is invisible from the screen — the button still succeeds and still reports
 * success — which is why a named test pins it rather than trusting review.
 * (This repo deliberately wraps no revoke-all at all; see `passkeyClient.ts`.)
 *
 * ── ⚠️ THREE HONESTY CONSTRAINTS, EACH FROM A VERIFIED SERVER PROPERTY ─────
 * Confirmed against the type's own documentation in
 * `@usacsmideo/types` → `functions/auth/passkeyList.d.ts`:
 *
 *  1. `deviceLabel` is CLIENT-SUPPLIED, best-effort and NOT identifying. Two
 *     devices can both self-label "iPhone" and nothing prevents it, so the
 *     ENROLLED DATE is what distinguishes rows. Never key a revoke off the label.
 *  2. We CANNOT mark "this device". The list response carries no session
 *     credential id, so there is nothing to derive it from. On a control whose
 *     whole purpose is *don't delete the one you're holding*, a guessed marker is
 *     worse than none — so the absence is stated in copy instead of papered over.
 *  3. `backupEligible` / `backupState` are OPTIONAL. Absent means "not
 *     recorded", NOT "device-bound". Absent renders as unknown, never as a claim.
 *
 * ── ⚠️ COPY: NEVER "FOR THIS DEVICE" ──────────────────────────────────────
 * A passkey enrolled in Chrome on ANDROID signed in on a MACBOOK — a synced
 * passkey belongs to the account's syncing ecosystem, not the enrolling device.
 * So: "Add a passkey", and "Passkey added." on success.
 *
 * ⚠️ The admin console still says "Passkey added for this device." at
 * `AdminPasskeyEnrollmentSection.tsx:337` — `fea2c489` fixed its heading and
 * button and missed the success banner, so the shipped console contradicts its
 * own stated constraint. That string is deliberately NOT ported (raised upstream
 * as F8). Likewise "Synced to your account", never "your password manager": the
 * flags say a credential IS syncable; they say nothing about WHAT syncs it.
 */

/**
 * ⚠️ PATIENT-FACING COPY. NEVER A CODE, ALWAYS A NEXT STEP. The console's
 * equivalent says "admin" throughout and refers to the Firebase console, which
 * patients do not have.
 */
function errorCopy(error: PasskeyEnrollmentError, variant: 'profile' | 'gate' = 'profile'): string {
  switch (error.kind) {
    case 'bad-password':
      return "That password didn't match. Please try again.";
    case 'stale-session':
      return "You've been here a little while, so we need your password once more. Please enter it again.";
    case 'too-many-requests':
      // No duration (CU-86ajtgauu): Google publishes none, and the one block we
      // have measured (MIDEO-UNIFIED-88) lasted HOURS, not minutes.
      return 'Too many attempts. For security, please wait a while before trying again.';
    case 'unsupported':
      return "This browser can't use passkeys. Try a current version of Chrome, Safari, Edge or Firefox.";
    case 'already-registered':
      // ⚠️ NOT "This device already has a passkey" (adversarial round 1, F3).
      // Two things were wrong with that. It broke this file's own banned-copy
      // rule, and it was semantically false: `webPasskeyProvider` maps this from
      // `InvalidStateError` on CREATE, which means "this AUTHENTICATOR already
      // holds a credential for this RP". For a synced passkey the authenticator
      // is the patient's cloud keychain, so this fires on a device that has
      // never enrolled anything — and "remove the existing one above" could
      // point at a row they do not recognise.
      //
      // So the copy names the account, not the device, and tells them what is
      // actually true: a usable passkey already exists, so there is nothing to
      // add here.
      // ⚠️ VARIANT-AWARE (adversarial round 1, F3). "remove that passkey below"
      // pointed at the credential registry — which is `variant === 'profile'`
      // only, so on the GATE it named a control that is not on screen. And this
      // branch is genuinely reachable there: for a SYNCED passkey the
      // authenticator is the patient's cloud keychain, so `InvalidStateError`
      // fires on a device that never enrolled anything (see the note above).
      //
      // On the gate the patient does not need to replace anything — they
      // already hold a usable credential, which is the way THROUGH the gate. So
      // the copy points back at the choice instead of at a missing list.
      return variant === 'gate'
        ? "Your account already has a passkey saved in this browser or password manager, so there's nothing to add. Go back and choose “Use your passkey” to sign in with it."
        : "Your account already has a passkey saved in this browser or password manager, so there's nothing to add. It should offer itself at the sign-in screen. To replace it, remove that passkey below and add a new one.";
    case 'network':
      // ⚠️ DISTINCT FROM generic BECAUSE THE REMEDY DIFFERS — "reconnect and
      // this will work" versus "try again or use another method".
      return "We couldn't reach the server. Please check your connection and try again.";
    case 'generic':
    default:
      return 'Something went wrong. Please try again in a moment, or contact info@mideohealth.com if it keeps happening.';
  }
}

/** Copy for a failed list/remove. `not-found` is expected, not a fault. */
function managementCopy(error: PasskeyManagementError): string {
  switch (error.kind) {
    case 'not-found':
      // ⚠️ MUST NOT ASSERT A REASON. The server returns ONE uniform failure for
      // unknown / not-yours / wrong-surface, precisely so it cannot be used to
      // probe what an account holds. "Refresh and try again" is the honest
      // ceiling.
      return "We couldn't find that passkey — it may already have been removed. Please refresh and try again.";
    case 'network':
      return "We couldn't reach the server. Please check your connection and try again.";
    case 'generic':
    default:
      return 'Something went wrong. Please try again in a moment.';
  }
}

/**
 * Enrolled date — ⚠️ THE RELIABLE DISCRIMINATOR, unlike the device label.
 * Write-once server-side, so it cannot drift.
 */
function formatEnrolled(epochMs: number): string {
  return new Date(epochMs).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * How a credential is stored, in plain language.
 *
 * ⚠️ THREE STATES, NOT TWO. `undefined` is its own answer — "we don't know" —
 * because the flags are optional in the wire type. Rendering absence as "this
 * device only" would tell a patient that a synced credential is bound to the
 * handset they just lost, which is precisely backwards.
 *
 * ⚠️ NOTE FOR THE E2E RUN (barrel F7): on the PORTAL, "Sync status not recorded"
 * is a REAL FINDING, not expected noise. The console runbook's caveat — that it
 * is normal for credentials enrolled before types 1.52.0 — does NOT transfer:
 * no portal passkey credential has ever existed, so there is no legacy cohort.
 */
function syncDescription(c: PasskeyCredentialSummary): string {
  if (c.backupEligible === undefined) return 'Sync status not recorded';
  if (c.backupEligible) {
    // ⚠️ "YOUR ACCOUNT", NOT "YOUR PASSWORD MANAGER". The flags say the
    // credential is backed up and syncable; they do NOT say what is doing the
    // syncing. A Chrome-stored passkey syncs to a Google account, and a patient
    // who never installed a password manager reasonably reads the old copy as
    // wrong or as describing someone else's setup.
    //
    // ⚠️ `backupState` is POINT-IN-TIME and never refreshed, so it is presented
    // as "when enrolled" rather than as current state.
    return c.backupState ? 'Synced to your account' : "Can sync, but wasn't backed up when enrolled";
  }
  // Hardware keys and platform authenticators with sync switched off. A real
  // minority, not a legacy case.
  return 'Saved on one device only';
}

export interface PasskeyEnrollmentSectionProps {
  /** Called once a passkey is live AND the credential list has been reloaded. */
  onEnrolled?: () => void;
  /**
   * ⚠️ Called on the `done` TRANSITION, before the list reload — for a host
   * whose reaction is the patient's next step rather than a derived refresh.
   * The gate uses this; the profile page uses `onEnrolled`. See the effect.
   */
  onEnrolledImmediate?: () => void;
  /**
   * ⚠️ ONE COMPONENT, TWO HOSTS — the same pattern `SmsEnrollmentSection` uses,
   * for the same reason: a second copy would drift on a security surface.
   *
   *  • `profile` — the full section, including the per-credential registry.
   *  • `gate`    — inside the PHI gate (CU-86ajzr9fp). Enrollment ONLY. The
   *    registry is hidden: a gated patient has no PHI access and is here to add
   *    ONE factor, and the registry's honest "we can't tell which passkey this
   *    browser is using" notice reads as nonsense against an empty list.
   */
  variant?: 'profile' | 'gate';
  /**
   * ⚠️ GATE ONLY, AND IT RETURNS TO THE FACTOR CHOICE — never out of the gate.
   * On the profile page there is nothing to cancel to, so it is not passed.
   */
  onCancel?: () => void;
}

export function PasskeyEnrollmentSection({
  onEnrolled,
  onEnrolledImmediate,
  variant = 'profile',
  onCancel,
}: PasskeyEnrollmentSectionProps) {
  const [password, setPassword] = React.useState('');
  const [removingCredId, setRemovingCredId] = React.useState<string | null>(null);
  /** Which row is showing its confirmation panel, if any. */
  const [confirmingCredId, setConfirmingCredId] = React.useState<string | null>(null);

  const {
    step,
    busy,
    error,
    resolver,
    supported,
    submitPassword,
    completeSecondFactor,
    reset,
    credentials,
    credentialsLoading,
    managementError,
    refreshCredentials,
    removeCredential,
  } = usePasskeyEnrollment();

  // Load the list once on mount. The ceremony is independent of it.
  //
  // ⚠️ `refreshCredentials` is deliberately NOT in the dependency array. It is
  // recreated whenever `deps` changes identity, and including it would re-run
  // this on every render — the "unstable callback identity" family that has cost
  // this workstream three separate defects.
  React.useEffect(() => {
    void refreshCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasCredentials = (credentials?.length ?? 0) > 0;

  /**
   * ⚠️ REPORT SUCCESS THE MOMENT IT HAPPENS, NOT AFTER THE LIST RELOADS —
   * staging 2026-08-13, the SECOND occurrence of the same defect.
   *
   * Every `onEnrolled?.()` call site sits behind `await refreshCredentials()`,
   * which is correct for the PROFILE page (the registry must be current before
   * the host reacts) and wrong for the GATE, where the host's reaction IS the
   * patient's way out. `step` flips to `'done'` the instant the ceremony
   * returns, so the green "Passkey added." panel renders immediately while
   * `onEnrolled` waits on a network round trip — leaving the patient looking at
   * a success box that appears to be the end of the road. Reported twice:
   * "still seeing green box only... had to navigate off and come back".
   *
   * ⚠️ AND IF THE REFRESH FAILS OR HANGS, `onEnrolled` NEVER FIRES AT ALL. The
   * handoff was strictly weaker than the panel it was meant to accompany.
   *
   * `onEnrolledImmediate` fires on the `done` TRANSITION itself — the same event
   * that renders the panel, so the two can no longer disagree. `onEnrolled`
   * still fires afterwards with the list refreshed, which is what the profile
   * page wants. Latched, so a re-render cannot fire it twice.
   */
  const reportedDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (step !== 'done') {
      reportedDoneRef.current = false;
      return;
    }
    if (reportedDoneRef.current) return;
    reportedDoneRef.current = true;
    onEnrolledImmediate?.();
    // ⚠️ `onEnrolledImmediate` deliberately absent from the deps — consumers
    // pass inline arrows, and including it would re-fire on every render. The
    // "unstable callback identity" family, which this workstream has now paid
    // for four times.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleRemove = async (credId: string) => {
    setConfirmingCredId(null);
    setRemovingCredId(credId);
    try {
      await removeCredential(credId);
    } finally {
      setRemovingCredId(null);
    }
  };

  return (
    <section className={variant === 'gate' ? 'flex flex-col gap-3' : 'flex flex-col gap-3 border-t border-gray-200 pt-6'}>
      <h3 className="text-lg font-semibold">
        {variant === 'gate' ? 'Set up a passkey' : 'Passkeys for your account'}
      </h3>
      <p className="text-sm">
        A passkey lets you sign in with your fingerprint, face or device PIN instead of a texted code.
      </p>

      {/* ⚠️ GATE OMITS THIS. It tells a patient what will happen at the NEXT
          sign-in, which is not the question in front of someone currently
          blocked from their records. The gate's own copy says what they need. */}
      {variant === 'profile' && (
        <>
      {/* ⚠️ PASSKEY SIGN-IN IS LIVE AS OF CU-86ajzr94n (sub-task 3, PR #278).
          This previously read "Signing in with a passkey is coming soon … you'll
          keep using your password and texted code until we turn it on", which
          was true when written and became FALSE the moment login shipped.

          It is corrected HERE, in the diff that falsifies it, on the same
          principle as `cf68967` — which corrected `authAssurance`'s
          "unreachable" comment for the same reason. ⚠️ A shipped falsehood on a
          security surface is worse than silence: a patient who reads "coming
          soon" on the page that offers the credential reasonably concludes
          enrolling is pointless, and does not enrol. */}
      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <span className="font-medium">You can sign in with a passkey.</span> Once you add one, your browser will offer
        it at the sign-in screen. Your password and texted code keep working as they do today.
      </div>
        </>
      )}

      {!supported && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          This browser can&apos;t use passkeys. Try a current version of Chrome, Safari, Edge or Firefox.
        </div>
      )}

      {/* ── The credential registry ───────────────────────────────────────
          ⚠️ PROFILE ONLY. On the gate the patient is blocked from their records
          and is here to add ONE factor — per-credential management is not the
          task, and the registry's honest "we can't tell which passkey this
          browser is using" notice reads as nonsense against an empty list.
          ⚠️ The remove control going with it is deliberate: destructive
          credential management does not belong on a surface a patient reached
          because they were locked out. */}
      {variant === 'profile' && (
      <div>
        <h4 className="text-base font-semibold">Your passkeys</h4>

        {credentialsLoading && credentials === null && <p className="mt-2 text-sm">Loading your passkeys…</p>}

        {credentials !== null && !hasCredentials && (
          <p className="mt-2 text-sm">You don&apos;t have any passkeys for MIDEO yet.</p>
        )}

        {hasCredentials && (
          <>
            {/* ⚠️ THE HONEST LIMITATION, STATED. We cannot mark which credential
                this browser is using — the list carries no session credential id.
                Saying so is better than a marker that could be wrong on the one
                control where being wrong means losing your own access. */}
            <p className="mt-2 text-sm text-gray-700">
              We can&apos;t tell which passkey this browser is using, so please check the enrolled date before removing
              one.
            </p>

            <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200">
              {credentials?.map((c) => (
                <li key={c.credId} className="flex flex-col gap-2 p-3">
                  <div className="flex flex-row items-start justify-between gap-3">
                    <div className="flex flex-col">
                      {/* The label is best-effort; it may be absent or duplicated. */}
                      <span className="text-sm font-medium">{c.deviceLabel?.trim() || 'Unnamed passkey'}</span>
                      <span className="text-sm text-gray-600">Enrolled {formatEnrolled(c.createdAt)}</span>
                      <span className="text-sm text-gray-600">{syncDescription(c)}</span>
                    </div>
                    {confirmingCredId !== c.credId && (
                      <button
                        type="button"
                        onClick={() => setConfirmingCredId(c.credId)}
                        disabled={removingCredId !== null}
                        className="text-sm text-red-700 underline disabled:no-underline disabled:opacity-60"
                      >
                        {removingCredId === c.credId ? 'Removing…' : 'Remove'}
                      </button>
                    )}
                  </div>

                  {/* ⚠️ SPELL OUT THE CONSEQUENCE BEFORE THE CLICK, and name
                      WHICH credential — the label alone can be ambiguous, so the
                      date is included. Rendered INLINE rather than in a modal:
                      antd's Modal is used nowhere in this repo, and an
                      imperative portal dialog would also render outside the tree
                      where a test could pass vacuously by querying the container
                      and finding nothing. */}
                  {confirmingCredId === c.credId && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3">
                      <p className="text-sm font-medium text-red-900">
                        Remove {c.deviceLabel?.trim() || 'this unnamed passkey'}, enrolled{' '}
                        {formatEnrolled(c.createdAt)}?
                      </p>
                      <p className="mt-2 text-sm text-red-900">
                        That passkey will no longer be able to sign in. Your other passkeys and your current session are
                        not affected.
                      </p>
                      {/* ⚠️ REVOKING HERE DOES NOT TOUCH THE LOCAL COPY, and that
                          gap is confusing precisely because it is invisible: the
                          passkey keeps appearing in the browser's picker at
                          sign-in and then fails with the server's uniform error,
                          which by design cannot say why. Say so BEFORE the click
                          rather than leaving a patient to discover a dead
                          credential later. (Console `fea2c489`.) */}
                      <p className="mt-2 text-sm text-red-900">
                        Remember to also delete it from your password manager or device — removing it here does not
                        remove the copy stored on your device.
                      </p>
                      <div className="mt-3 flex flex-row gap-3">
                        <button
                          type="button"
                          onClick={() => void handleRemove(c.credId)}
                          className="text-sm font-medium text-red-700 underline"
                        >
                          Remove this passkey
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmingCredId(null)}
                          className="text-sm underline"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {managementError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {managementCopy(managementError)}
          </p>
        )}
      </div>
      )}

      {/* ── Add a passkey ───────────────────────────────────────────────── */}
      {step !== 'needs-second-factor' && step !== 'done' && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (busy || !password || !supported) return;
            void (async () => {
              const ok = await submitPassword(password);
              if (ok) {
                setPassword('');
                await refreshCredentials();
                onEnrolled?.();
              }
            })();
          }}
        >
          {/* ⚠️ NOT "Add a passkey for this device" — see the header. */}
          <h4 className="text-base font-semibold">Add a passkey</h4>
          <p className="text-sm">
            For your security, we confirm your password before adding a sign-in method. Your browser will then ask for
            your fingerprint, face or device PIN.
          </p>
          <PasswordInput
            id="passkey-enroll-password"
            title="Confirm your password"
            password={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            id="passkey-enroll-submit"
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              if (busy || !password || !supported) return;
              void (async () => {
                const ok = await submitPassword(password);
                if (ok) {
                  setPassword('');
                  await refreshCredentials();
                  onEnrolled?.();
                }
              })();
            }}
            textContent="Add a passkey"
            disabled={busy || !password || !supported}
            working={busy}
            workingText="Working..."
          />
        </form>
      )}

      {/* ── Done ────────────────────────────────────────────────────────── */}
      {step === 'done' && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3">
          {/* ⚠️ "Passkey added." — NOT "for this device", and NOT "Add another
              device". A synced passkey is not scoped to the enrolling device.
              The console's own success banner still gets this wrong (F8). */}
          <p className="text-sm font-medium text-green-900">Passkey added.</p>

          {/* ⚠️ THE TWO HOSTS NEED DIFFERENT SUCCESS COPY, AND THE GATE'S WAS
              WRONG IN THREE WAYS (staging, 2026-08-13):

               1. "It's listed above" — the registry is PROFILE-ONLY, so on the
                  gate it named a list that is not on screen.
               2. "your browser will offer it at the sign-in screen" — true
                  eventually, but the patient is being gated NOW; it described a
                  future visit instead of the way out of this one.
               3. "your password and texted code keep working as they do today"
                  — addressed to someone who HAS SMS. A patient reaching this
                  gate with neither factor never had a texted code, so it reads
                  as a reference to something they do not have.

              On the gate the only thing that matters is the next step, and
              ⚠️ ENROLLING DOES NOT SATISFY THE GATE: `passkeyRegister` does not
              mint `aal2` — only `passkeyVerify` does. So the patient must still
              USE the credential they just made. That is what this panel says,
              and `onEnrolled` drives it (see `MfaGateFactorChoice`). */}
          {variant === 'gate' ? (
            <p className="mt-2 text-sm text-green-900">
              Now confirm it to continue — your browser will ask for your fingerprint, face or device PIN.
            </p>
          ) : (
            <p className="mt-2 text-sm text-green-900">
              It&apos;s listed above, and your browser will offer it at the sign-in screen. Your password and texted
              code keep working as they do today.
            </p>
          )}

          {/* ⚠️ "Add another passkey" IS PROFILE-ONLY. On the gate it offered a
              second credential to someone who has not yet used their first and
              is still locked out — a detour presented as the next step. */}
          {variant === 'profile' && (
            <button type="button" onClick={reset} className="mt-2 text-sm text-green-900 underline">
              Add another passkey
            </button>
          )}
        </div>
      )}

      {/* ── The SMS second-factor challenge, INLINE ─────────────────────── */}
      {step === 'needs-second-factor' && resolver && (
        <div className="flex flex-col gap-3">
          {/* ⚠️ SAY WHY THE CODE IS BEING ASKED FOR, AND THAT THE PASSWORD WAS
              CORRECT. Without this a patient sees an SMS prompt appear after
              typing their password and reasonably reads it as a rejection. */}
          <p className="text-sm">
            <span className="font-medium">One more step.</span> Your password was correct. Because your account uses a
            texted code, please confirm that too and we&apos;ll finish adding your passkey.
          </p>
          {/* ⚠️ RENDERED INLINE, NOT NAVIGATED TO. The resolver is a live IdP
              object that cannot survive serialization. Rendering it here also
              replaces the password step rather than stacking over it, so a
              second submit cannot fire against a pending resolver.

              ⚠️ `onComplete` continues with `completeSecondFactor()`, which does
              NOT re-drive the reauth — see that function's note. It is also
              guarded at BOTH ends: this form fires it exactly once per challenge
              (CU-86ajzr8x1 commit 1), and the hook holds its own ref latch,
              because a hook cannot assume its caller is idempotent. */}
          <MfaChallengeForm
            resolver={resolver}
            onComplete={() => {
              void (async () => {
                const ok = await completeSecondFactor();
                if (ok) {
                  setPassword('');
                  await refreshCredentials();
                  onEnrolled?.();
                }
              })();
            }}
            onCancel={() => {
              // ⚠️ NEVER TRAP THE PATIENT. Cancelling drops the pending resolver
              // and returns to a clean panel with nothing half-done. Their
              // password still works as the durable root.
              setPassword('');
              reset();
            }}
          />
          {/* ⚠️ NO reCAPTCHA HOST CONTAINER HERE, DELIBERATELY (adversarial
              round 1, F4). `MfaChallengeForm` owns its own container: it
              hardcodes `mfa-challenge-recaptcha` internally, renders the
              `-host` wrapper itself, and accepts no id prop.

              This section previously rendered a `passkey-enroll-recaptcha-host`
              div and defined a constant for it, imitating `SmsEnrollmentSection`
              — which legitimately has one because it passes its id INTO
              `useSmsEnrollment`. Here nothing read it, so it was dead markup
              that read as wiring. Do not re-add one; if a container is ever
              needed on this surface, wire it through a prop and prove it with a
              test rather than rendering a div that looks connected. */}
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-700">
          {errorCopy(error, variant)}
        </p>
      )}

      {/* ⚠️ RETURNS TO THE FACTOR CHOICE, NOT OUT OF THE GATE. Hidden once the
          ceremony is `done`, where there is nothing to back out of — matching
          `SmsEnrollmentSection`'s rule for the same control. */}
      {onCancel && step !== 'done' && (
        <button
          type="button"
          onClick={onCancel}
          className="self-start text-sm underline"
          disabled={busy}
          data-testid="passkey-enroll-cancel"
        >
          Choose a different method
        </button>
      )}
    </section>
  );
}

export default PasskeyEnrollmentSection;
